import React, { cloneElement } from 'react';
import { Button, Modal, FormControl, ControlLabel, Alert, FormGroup, Checkbox } from "react-bootstrap";
import DatePicker from "react-bootstrap-date-picker";
import moment from 'moment';
import immutable from "immutable";
import util from "./../../lib/util.js";
import context from "./../../lib/context.js";
import ajax from "./../../lib/ajax.js";
import CodeList from "./../shared/codelist.jsx";
import { PTO_STATUS } from './_components/_common.js';
import DynamicContent from '../../lib/components/dynamic-content.jsx';

export default class PtoDetail extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            resource: null,
            errors: [],
            isPeriod: false,
            pto: null,
            ptoLines: [],
            ptoTypes: [],
            ptoReasons: [],
            accrualBalances: [],
            maxPeriodInWeeks: 3,
            minHoursPerDay: 8,
            maxHoursPerDay: 8,
            availableHours: 0,
            scheduledHours: 0,
            canBeDenied: false,
            fullDayIncrement: true
        }
        this.handleChange = this.handleChange.bind(this);
        this.togglePeriod = this.togglePeriod.bind(this);
        this.getAvailableHours = this.getAvailableHours.bind(this);
        this.save = this.save.bind(this);
        this.close = this.close.bind(this);
        this.approve = this.approve.bind(this);
        this.deny = this.deny.bind(this);
        this.consult = this.consult.bind(this);
        this.renderExpress = this.renderExpress.bind(this);
    }

    componentDidMount() {
        this.load(this.props.ptoId);
    }

    load(id) {
        const self = this;
        const clone = immutable.fromJS(self.state);
        const userRole = (this.props.userRole || "").toLowerCase();
        clone.action = this.props.action;
        clone.canBeDenied = this.props.canBeDenied;

        if (clone.action == "approve" || clone.action == "deny" || clone.action == "consult" || clone.action == "cancel") {
            clone.pto = { ptoId: id }
            self.setState(clone);
            return;
        }

        context.message({
            show: true,
            type: "loading",
            message: "Loading. Please wait..."
        });

        let url = `pto/${id}`;
        if (clone.action == "create") {
            url = "pto-new";
            // if creation is initiated by SUPERVISOR or PAYROLL-ADMIN, timecard ID is passed
            if (userRole == "supervisor" || userRole == "payroll-admin")
                url = url + `/${self.props.timecardId}`;
        }

        const apiCalls = [
            ajax.api().get('get-pto-resource'),
            ajax.vm().get(url)
        ];

        // we want to know if supervisor can DECLINE or just CONSULT HR
        if (id != 0 && (userRole == "supervisor" || userRole == "payroll-admin") && clone.canBeDenied == null)
            apiCalls.push(ajax.api().get('get-pto-deniable'));

        Promise.all(apiCalls)
            .then(function (result) {
                clone.resource = result[0].data;

                const res = result[1];
                clone.pto = res.data.pto;
                clone.ptoLines = res.data.ptoLines;
                
                let sickAccruals = res.data.accrualBalances.filter(o => o.accrualCode === "SICKLV");
                if (sickAccruals.length == 1 && sickAccruals[0].availableHours > 0) {
                    clone.ptoTypes = res.data.ptoTypes.filter(o => o.code !== "FTOSICK");
                } else {
                    clone.ptoTypes = res.data.ptoTypes;
                }

                clone.ptoReasons = res.data.ptoReasons;
                clone.accrualBalances = res.data.accrualBalances;
                clone.maxPeriodInWeeks = res.data.maxPeriodInWeeks;
                clone.minHoursPerDay = res.data.minHoursPerDay;
                clone.maxHoursPerDay = res.data.maxHoursPerDay;

                // set default Type
                if (id == 0 && self.props.defaultType != null && self.props.defaultType.trim() != "") {
                    let sel = clone.ptoTypes.filter(o => o.code == self.props.defaultType.trim());
                    if (sel.length > 0) {
                        clone.pto.code = sel[0].code;
                        clone.pto.description = sel[0].codeName;
                        clone.pto.leaveGroup = sel[0].propertyValue; // pto.leaveGroup field not in DB, only used here
                    }
                }
                else if (id > 0) {
                    let sel = clone.ptoTypes.filter(o => o.code == clone.pto.code);
                    if (sel.length > 0)
                        clone.pto.leaveGroup = sel[0].propertyValue; // pto.leaveGroup field not in DB, only used here
                    else
                        clone.ptoTypes.push({
                                code: clone.pto.code,
                                codeName: clone.pto.description
                            }); // PTO code no longer exists in the Time-Off Types dropdown, add it
                }

                clone.scheduledHours = clone.pto.unit;
                if (id == 0)
                    clone.availableHours = self.getAvailableHours(clone.pto.leaveGroup, clone.accrualBalances);
                else
                    clone.availableHours = self.getAvailableHours(clone.pto.leaveGroup, clone.accrualBalances) + clone.scheduledHours; // when editing, combine available and scheduled hours

                clone.pto.fromDate = moment(res.data.pto.fromDate).format("YYYY-MM-DD");
                clone.pto.toDate = moment(res.data.pto.toDate).format("YYYY-MM-DD");

                clone.isPeriod = clone.pto.fromDate !== clone.pto.toDate;
                if (clone.ptoLines.length > 0) {
                    clone.pto.unitPerDay = clone.pto.unit / clone.ptoLines.length; // this field not in DB but will be used as PTOLine unit

                    // format all action dates to string
                    for (let i = 0; i < clone.ptoLines.length; i++)
                        clone.ptoLines[i].actionDate = moment(clone.ptoLines[i].actionDate).format("YYYY-MM-DD");
                }

                if (result.length == 3) {
                    clone.canBeDenied = result[2].data.filter(o => o.typeCode == clone.pto.code && o.reasonCode == clone.pto.reasonCode).length > 0;
                }

                clone.fullDayIncrement = true; //per Ming: if field not in API, default is TRUE
                let sel = clone.accrualBalances.filter(o => o.leaveGroup == clone.pto.leaveGroup);
                if (sel.length > 0)
                    clone.fullDayIncrement = sel[0].fullDayIncrement;
                
                self.setState(clone, () => context.message({ show: false }));
            })
            .catch(function (err) {
                let data = err.response ? err.response.data : err;
                context.message({
                    show: true,
                    type: "error",
                    message: data.exceptionMessage || data.message,
                    buttons: "close"
                }, self.close);
            });
    }

    save(action) {
        const self = this;
        const ptoData = {
            pto: this.state.pto,
            ptoLines: this.state.ptoLines,
            action: action
        };

        const errors = this.validate(ptoData, this.props.userRole);
        this.setState({ errors: errors });
        if (errors.length > 0)
            return;

        let msgProcessing = "Saving. Please wait...";
        let msgProcessed = "Saved successfully.";
        if (action == "cancel") {
            msgProcessing = "Cancelling. Please wait...";
            msgProcessed = "The request is cancelled.";
        }

        context.message({
            show: true,
            type: "loading",
            message: msgProcessing
        });

        const url = ptoData.action == "create" ? 'create-pto' : 'update-pto';
        const payload = {
            ptoId: ptoData.pto.ptoId,
            peopleId: ptoData.pto.peopleId,
            code: ptoData.pto.code,
            description: ptoData.pto.description,
            reasonCode: ptoData.pto.reasonCode,
            unitPerDay: ptoData.pto.unitPerDay,
            notes: ptoData.pto.notes,
            supervisorNotes: ptoData.pto.supervisorNotes,
            reasonForChange: null,
            action: ptoData.action,
            actionDates: ptoData.ptoLines ? ptoData.ptoLines.map(o => o.actionDate) : []
        };

        ajax.api()
            .post(url, payload)
            .then(function (res) {
                if (res.data != null && res.data != '')
                    msgProcessed = res.data;
                
                context.message({
                    show: true,
                    type: "info",
                    message: msgProcessed,
                    buttons: "close"
                }, () => {
                    if (self.props.onSaved) self.props.onSaved();
                });
            })
            .catch(function (err) {
                let data = err.response ? err.response.data : err;

                if (data.ClassName !== "ActionPortal.ValidationException") {
                    var msg = data.exceptionMessage || data.message;

                    // if error message contains leave group code, replace with description
                    const ptoTypes = self.state.ptoTypes.sort((a, b) => (b.propertyValue.length - a.propertyValue.length));
                    for (var i=0; i < ptoTypes.length; i++) {
                        if (msg.includes(ptoTypes[i].propertyValue)) {
                            msg = msg.replace(new RegExp(ptoTypes[i].propertyValue, 'g'), ptoTypes[i].codeName);
                            break;
                        }
                    }

                    context.message({
                        show: true,
                        type: "error",
                        message: msg,
                        buttons: "close"
                    });
                    return;
                }

                let arrErr = [];
                for (let key in data.Data.summary.validationResults)
                    arrErr.push(data.Data.summary.validationResults[key]);

                self.setState({ errors: arrErr });
                context.message({
                    show: false,
                });
            });
    }

    close() {
        if (this.props.onClosed)
            this.props.onClosed();
    }

    approve() {
        // if (this.props.switchToApproveOrDeny) 
        //     this.props.switchToApproveOrDeny("approve");
        this.save("approve");
    }

    deny() {
        //if (this.props.switchToApproveOrDeny) 
        //    this.props.switchToApproveOrDeny("deny");
        this.save("deny");
    }

    consult() {
        //if (this.props.switchToApproveOrDeny) 
        //    this.props.switchToApproveOrDeny("consult");
        this.save("consult");
    }

    handleChange(id, val) {
        const self = this;
        const clone = immutable.fromJS(this.state.pto).toJS();
        clone[id] = val;

        if (id == "fromDate" || id == "toDate") {

            if (id == "fromDate") {
                if (!self.state.isPeriod)
                    clone.toDate = clone.fromDate;

                if (clone.fromDate > clone.toDate)
                    clone.toDate = moment(clone.fromDate).add(1, "day").format("YYYY-MM-DD");
            }
            else if (clone.toDate < clone.fromDate) {
                clone.toDate = self.state.pto.toDate;
                self.setState({ pto: clone, errors: [] });
                return false;
            }

            const dToDate = moment(clone.toDate, "YYYY-MM-DD");
            const dFromDate = moment(clone.fromDate, "YYYY-MM-DD");

            if (util.countWeekending(dFromDate, dToDate) > self.state.maxPeriodInWeeks) {
                self.setState({ errors: [`Maximum period is ${self.state.maxPeriodInWeeks} weeks`] })
                return;
            }

            let cloneLines = [];
            //let cloneLines = immutable.fromJS(self.state.ptoLines).toJS();
            //cloneLines = cloneLines.filter(o => clone.fromDate <= o.actionDate && o.actionDate <= clone.toDate);

            if (clone.fromDate == clone.toDate)
                cloneLines = [{ ptoLineId: 0, actionDate: clone.fromDate }]; // must have atleast 1 PTO Line
            else
                self.addWeekdaysAsPtoLines(dFromDate, dToDate, cloneLines);

            self.setState({
                pto: clone,
                ptoLines: cloneLines,
                scheduledHours: cloneLines.length * clone.unitPerDay,
                isPeriod: clone.fromDate !== clone.toDate,
                errors: []
            });
        }
        else if (id == "unitPerDay") {
            if (clone.unitPerDay.indexOf('h') > -1 || clone.unitPerDay.indexOf('m') > -1) {
                clone.unitPerDay = util.hmStringToUnit(clone.unitPerDay);
            }

            self.setState({
                pto: clone,
                scheduledHours: self.state.ptoLines.length * clone.unitPerDay,
                errors: []
            });
        }
        else if (id == "code") {
            let sel = self.state.ptoTypes.filter(o => o.code == val);
            clone.description = (val == "") ? "" : sel[0].codeName;
            clone.leaveGroup = (val == "") ? "" : sel[0].propertyValue;
            clone.reasonCode = "";

            let fullDayIncrement = true; //per Ming: if field not in API, default is TRUE
            sel = self.state.accrualBalances.filter(o => o.leaveGroup == clone.leaveGroup);
            if (sel.length > 0)
                fullDayIncrement = sel[0].fullDayIncrement;

            self.setState({
                pto: clone,
                availableHours: this.getAvailableHours(clone.leaveGroup, self.state.accrualBalances),
                fullDayIncrement: fullDayIncrement,
                errors: []
            });
        }
        else {
            self.setState({ pto: clone, errors: [] });
        }
    }

    handleLineChange(evt, obj) {
        // handle change in PTO Lines selection
        let cloneLines = immutable.fromJS(this.state.ptoLines).toJS();

        if (evt.target.checked) {
            if (cloneLines.filter(o => o.actionDate === obj.actionDate).length == 0)
                cloneLines.push({
                    ptoLineId: 0,
                    ptoId: this.state.pto.ptoId,
                    unit: this.state.pto.unitPerDay,
                    code: this.state.pto.code,
                    description: this.state.pto.description,
                    actionDate: obj.actionDate,
                    weekendDate: obj.weekendDate
                });
        }
        else {
            cloneLines = cloneLines.filter(o => o.actionDate !== obj.actionDate);
        }

        this.setState({
            ptoLines: cloneLines,
            scheduledHours: cloneLines.length * this.state.pto.unitPerDay
        });
    }

    togglePeriod() {
        const isPeriod = !this.state.isPeriod;
        let fromDate = moment(this.state.pto.fromDate).clone();
        if (isPeriod)
            this.handleChange("toDate", fromDate.add(1, "day").format("YYYY-MM-DD"));
        else
            this.handleChange("toDate", this.state.pto.fromDate);
    }

    getAvailableHours(leaveGroup, accrualBalances) {
        let summ = 0;
        var balances = accrualBalances.filter(o => o.leaveGroup == leaveGroup);
        if (balances.length == 0)
            return summ;

        if (balances[0].capType == "BALANCE") {
            summ = balances[0].availableHours - balances[0].capHours;
            return summ;
        }
        
        if (balances[0].capType == "TAKEN") {
            summ = balances[0].capHours - balances[0].loggedHours;
            return summ;
        }
        
        balances.forEach(o => { summ += o.availableHours });
        return summ;
    }

    validate(obj, userRole) {
        userRole = userRole.toLowerCase();
        let msg = [];

        if (!obj.pto) {
            msg.push("Time-Off is missing");
            return msg;
        }

        // for CANCEL: if done by supervisor, reason is required
        if (obj.action == "cancel" && userRole  == "people") {
            return msg;
        }

        // for express APPROVE, DENY, CONSULT, CANCEL, we only require supervisor notes
        if ((obj.action == "approve" || obj.action == "deny" || obj.action == "consult" || obj.action == "cancel")
            && (obj.pto.code == null || obj.pto.code.trim().length == 0)) // indicates user is in EXPRESS mode
        {
            if (obj.pto.supervisorNotes == null || obj.pto.supervisorNotes.trim().length == 0) {

                let error = {
                    approve: "Supervisor Notes is required",
                    deny: "Reason for Declining is required",
                    consult: "Reason for Consulting HR is required",
                    cancel: "Reason for Cancelling is required"
                }
                msg.push(error[obj.action]);
            }

            return msg;
        }

        if (obj.pto.code == null || obj.pto.code.trim().length == 0) {
            msg.push("Type is required");
        }

        if ((obj.pto.reasonCode == null || obj.pto.reasonCode.trim().length == 0) && this.state.ptoReasons.filter(o => o.propertyValue == obj.pto.code).length > 0) {
            msg.push("Reason is required");
        }

        if (obj.pto.fromDate == null || obj.pto.fromDate.trim().length == 0) {
            msg.push("From date is required");
        }
        else if (!moment(obj.pto.fromDate)) {
            msg.push("From date is invalid");
        }

        if (obj.pto.toDate == null || obj.pto.toDate.trim().length == 0) {
            msg.push("To date is required");
        }
        else if (!moment(obj.pto.toDate)) {
            msg.push("To date is invalid");
        }
        else if (obj.pto.fromDate != null && moment(obj.pto.fromDate) && moment(obj.pto.fromDate).format("YYYY-MM-DD") > moment(obj.pto.toDate).format("YYYY-MM-DD")) {
            msg.push("From Date must be before To Date");
        }

        if (obj.pto.unitPerDay == null) {
            msg.push("Hours is required");
        }
        else if (obj.pto.unitPerDay < this.state.minHoursPerDay) {
            msg.push(`Hours must not be lower than ${this.state.minHoursPerDay}`);
        }
        else if (obj.pto.unitPerDay > this.state.maxHoursPerDay) {
            msg.push(`Hours must not be higher than ${this.state.maxHoursPerDay}`);
        }

        if (this.state.scheduledHours > this.state.availableHours) {
            var arrBalances = this.state.accrualBalances.filter(o => o.leaveGroup == obj.pto.leaveGroup);
            if (arrBalances.length > 0 && !util.isStringNullOrEmpty(arrBalances[0].capType))
                msg.push(`${obj.pto.description} has capped out per policy.`);
            else
                msg.push(`You are attempting to enter more hours for ${obj.pto.description} than you have available. Please re-enter.`);
        }

        // if (obj.pto.notes == null || obj.pto.notes.trim().length == 0)
        //     msg.push("Reason for Request is required");

        if (userRole != "people" && (obj.pto.supervisorNotes == null || obj.pto.supervisorNotes.trim().length == 0))
            msg.push("Supervisor Notes is required");

        if (obj.ptoLines == null || obj.ptoLines.length == 0)
            msg.push("At least one date must be selected");

        return msg;
    }

    generateDateList(dateStart, dateEnd, ptoLines) {
        let dateList = [];
        let i = 0
        let dte = moment(dateStart, "YYYY-MM-DD");
        let dEnd = moment(dateEnd, "YYYY-MM-DD");
        while (dte <= dEnd) {
            const sDate = dte.clone().format("YYYY-MM-DD");
            const ptoLine = this.getPtoLine(sDate, ptoLines);

            dateList.push({
                ordinal: i++,
                actionDate: sDate,
                weekendDate: moment(sDate).endOf("week").format("YYYY-MM-DD"),
                ptoLineId: ptoLine ? ptoLine.ptoLineId : -1,  // 0=selected but not saved, -1=not selected, > 0=selected and saved
                selected: ptoLine != null
            });
            dte.add(1, 'day');
        }

        // automatically SELECT if only 1 PTO Line (this will not display but needed when saving)
        if (dateList.length == 1 && dateList[0].ptoLineId == -1) {
            dateList[0].ptoLineId = 0;
            dateList[0].selected = true;
        }

        return dateList;
    }

    addWeekdaysAsPtoLines(dateStart, dateEnd, ptoLines) {
        let dte = dateStart.clone();
        let dEnd = dateEnd.clone();
        while (dte <= dEnd) {
            const sDate = dte.clone().format("YYYY-MM-DD");
            const ptoLine = this.getPtoLine(sDate, ptoLines);

            if (ptoLine == null && dte.day() > 0 && dte.day() < 6) {
                ptoLines.push({
                    ptoId: this.state.pto.ptoId,
                    unit: this.state.pto.unitPerDay,
                    code: this.state.pto.code,
                    description: this.state.pto.description,
                    actionDate: sDate,
                    weekendDate: moment(sDate).endOf("week").format("YYYY-MM-DD"),
                    ptoLineId: 0  // 0=selected but not saved, -1=not selected, > 0=selected and saved
                });
            }

            dte.add(1, 'day');
        }
    }

    getPtoLine(sDate, ptoLines) {
        for (var i = 0; i < ptoLines.length; i++) {
            if (sDate === ptoLines[i].actionDate)
                return ptoLines[i];
        }
        return null;
    }

    renderExpress() {
        const isMobile = this.isIos() || this.isAndroid();
        const clsMain = isMobile ? "ap-pto-detail mobile" : "ap-pto-detail";
        const res = this.state.resource;
        const pto = this.state.pto;
        const action = this.props.action;
        const errors = this.state.errors;
        const userRole = (this.props.userRole || "").toLowerCase();
        const isSupervisor = (userRole == "supervisor" || userRole == "payroll-admin");

        let buttonLabel = "Save";
        let notesLabel = "Notes";

        if (action == "approve") {
            notesLabel = "Supervisor Notes";
            buttonLabel = "Approve";
        }
        else if (action == "deny") {
            notesLabel = "Reason for Declining Time-Off";
            buttonLabel = "Decline";
        }
        else if (action == "consult") {
            notesLabel = "Reason to Consult HR";
            buttonLabel = "Send";
        }
        else if (action == "cancel" && isSupervisor) {
            notesLabel = "Reason for Cancelling Time-Off Request";
            buttonLabel = "Submit";
        }
        else if (action == "cancel") {
            buttonLabel = "Continue";
        }

        return (
            <div className={clsMain}>
                <div className="row">
                    {
                        action == "cancel" && isSupervisor == false ?
                            <div className="col-xs-12 col-md-12 ap-text-center">
                                <b>This will cancel the Time-Off request.<br />
                                    Are you sure you want to continue?</b>
                            </div>
                            :
                            action == "deny" && this.props.defaultType.includes("FTO")? 
                                <div className="col-xs-12 col-md-12">
                                    <ControlLabel>Reason for Declining FTO: <span className="ap-color-red">*</span></ControlLabel>
                                    <CodeList id="supervisorNotes" category="TIME_OFF_DENY_REASON" allText=""
                                        onChange={(code, codeName) => this.handleChange("supervisorNotes", codeName)} /> 
                                </div>
                                :
                                <div className="col-xs-12 col-md-12">
                                    <ControlLabel>{notesLabel}: <span className="ap-color-red">*</span></ControlLabel>
                                    <FormControl componentClass="textarea" rows="4" defaultValue={pto ? pto.supervisorNotes : null}
                                        onChange={(evt) => this.handleChange("supervisorNotes", evt.target.value)} />
                                </div>
                    }
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-12 button-panel">
                        <Button onClick={() => this.save(action)}>{buttonLabel}</Button>
                        <Button onClick={this.close}>Cancel</Button>
                    </div>
                </div>
                <div className={errors.length > 0 ? "row" : "ap-hidden"}>
                    <div className="col-xs-12 col-md-12 error-panel">
                        <Alert bsStyle="danger">
                            <ul>{errors.map(m => <li key={m}>{m}</li>)} </ul>
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }

    isAndroid() {
        var ua = navigator.userAgent.toLowerCase();
        return ua.indexOf("android") > -1;
    }

    isIos() {
        var ua = navigator.userAgent.toLowerCase();
        return ua.indexOf('ipad') > - 1 || ua.indexOf('iphone') > -1;
    }

    render() {
        const isMobile = this.isIos() || this.isAndroid();
        const clsMain = isMobile ? "ap-pto-detail mobile" : "ap-pto-detail";
        const action = this.props.action;
        if (action == "approve" || action == "deny" || action == "consult" || action == "cancel")
            return this.renderExpress();

        const userRole = (this.props.userRole || "").toLowerCase();
        const isSupervisor = (userRole == "supervisor" || userRole == "payroll-admin");
        const isPeriod = this.state.isPeriod;
        const res = this.state.resource;
        const pto = this.state.pto;
        const ptoLines = this.state.ptoLines;
        const errors = this.state.errors;
        const ptoTypes = this.state.ptoTypes;
        const fullDayIncrement = this.state.fullDayIncrement;
        const readonly = !!(pto && pto.statusCode && pto.statusCode != PTO_STATUS.Requested);
        
        if (pto == null)
            return null;

        const ptoReasons = this.state.ptoReasons.filter(o => o.propertyValue == pto.code);
        const showReason = (ptoReasons.length > 0);

        const showPtoTypeCaption = (!isSupervisor && pto.code != "");
        const showPtoUnitCaption = (!isSupervisor && pto.code != "");

        const ptoTypeCaption = showPtoTypeCaption ? res[`typeCaption${pto.code}`] : "";
        const ptoUnitCaption = showPtoUnitCaption ? res[`unitCaption${pto.code}`] : "";

        const availableHours = (this.state.availableHours % 1 == 0) ? this.state.availableHours : this.state.availableHours.toFixed(2);
        const scheduledHours = (this.state.scheduledHours % 1 == 0) ? this.state.scheduledHours : this.state.scheduledHours.toFixed(2);

        var accrualSummary = null;
        if (!util.isStringNullOrEmpty(pto.leaveGroup)) {
            var arrSelection = this.state.accrualBalances.filter(o => o.leaveGroup == pto.leaveGroup);
            if (arrSelection.length > 0) {
                let loggedHours = 0;
                arrSelection.forEach(o => { loggedHours += o.loggedHours });

                accrualSummary = arrSelection[0].isVisibleToUser == true?
                            <div className="col-xs-12 col-md-12 dashboard">
                                <div className="row">
                                    <div className="col-xs-12 col-md-12">
                                        <b>{pto.description} Balance</b>
                                    </div>
                                </div>
                                <div className="row dashboard-header">
                                    <div className="col-xs-4 col-md-4">
                                        Accrued Hours
                                    </div>
                                    <div className="col-xs-4 col-md-4">
                                        Taken Hours
                                    </div>
                                    <div className="col-xs-4 col-md-4">
                                        Available Hours
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-4 col-md-4">
                                        {availableHours}
                                    </div>
                                    <div className="col-xs-4 col-md-4">
                                        {scheduledHours}
                                    </div>
                                    <div className="col-xs-4 col-md-4">
                                        <b>{availableHours - scheduledHours}</b>
                                    </div>
                                </div>
                            </div>
                            :
                            util.isStringNullOrEmpty(arrSelection[0].capType) == false ? <div>You have taken <b>{util.toShortHmString(loggedHours)}</b> of <b>{pto.description}</b> for this anniversary year.</div> : null
            }
        }

        // const visibleBalances = util.isStringNullOrEmpty(pto.leaveGroup) ? [] 
        //                         : 
        //                         this.state.accrualBalances.filter(o => o.leaveGroup == pto.leaveGroup && o.isVisibleToUser === true).map((o, idx) =>
        //                             <div className="row" key={idx}>
        //                                 <div className="col-xs-4 col-md-4">
        //                                     {o.accrualDescription}
        //                                 </div>
        //                                 <div className="col-xs-3 col-md-3">
        //                                     {o.totalAccruedHours}
        //                                 </div>
        //                                 <div className="col-xs-2 col-md-2">
        //                                     {o.loggedHours}
        //                                 </div>
        //                                 <div className="col-xs-3 col-md-3">
        //                                     <b>{o.availableHours}</b>
        //                                 </div>
        //                             </div>
        //                         );

        let fromDate = pto.fromDate;
        let toDate = pto.toDate;

        const dateList = this.generateDateList(pto.fromDate, pto.toDate, ptoLines);

        let weekendDates = [];
        for (let i = 0; i < dateList.length; i++) {
            if (weekendDates.filter(w => dateList[i].weekendDate === w).length == 0)
                weekendDates.push(dateList[i].weekendDate);
        }
        const weeks = !isPeriod ? [] :
            weekendDates.map(w => {
                const dates = dateList.filter(d => w === d.weekendDate);
                return (
                    <div className="col-xs-6 col-md-4" key={w}>
                        Period Ending {moment(w).format('M/D/YY')} <br />
                        <div className="pto-lines-box">
                            {
                                dates.map(d => {
                                    const dDate = moment(d.actionDate);
                                    return <Checkbox key={d.ordinal} value={dDate.format('MM/DD/YYYY')} disabled={readonly}
                                        id={`actionDate_${d.ordinal}`} checked={d.selected} className={dDate.format('dddd')}
                                        onChange={(evt) => this.handleLineChange(evt, d)}>
                                        {dDate.format('ddd - M/D/YY')}</Checkbox>;
                                })
                            }
                        </div>
                    </div>)
            });

        const btn = {
            Approve: <Button disabled={readonly} onClick={this.approve} key="btn-approve">Approve</Button>,
            Cancel: <Button onClick={this.close} key="btn-close">Cancel</Button>,
            Save: <Button disabled={readonly} onClick={() => this.save(action)} key="btn-edit">
                {action == "create" ? "Send Request" : "Save"}
            </Button>,
            Deny: this.state.canBeDenied ?
                <Button disabled={readonly} onClick={this.deny} key="btn-deny">Decline</Button>
                :
                <Button disabled={readonly} onClick={this.consult} key="btn-deny">Consult HR</Button>
        };

        const arrButtons = (isSupervisor && this.props.switchToApproveOrDeny) ?
            [btn.Approve, btn.Save, btn.Deny, btn.Cancel]
            :
            [btn.Save, btn.Cancel];

        return (
            <div className={clsMain}>
                <div className="row">
                    <div className="col-xs-12 col-md-12" style={{ marginTop: "-10px" }}>
                        <ControlLabel className="pull-right required caption">* Required</ControlLabel>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-6">
                        <ControlLabel>Type of Time-Off: <span className="ap-color-red">*</span></ControlLabel>
                        <FormControl componentClass="select" style={{ marginBottom: "0px" }}
                            defaultValue={pto.code}
                            disabled={readonly || pto.ptoId > 0}
                            onChange={(evt) => this.handleChange("code", evt.target.value)}>
                            <option key="" value="">-- Please Select --</option>
                            {ptoTypes.map(o => <option key={o.code} value={o.code}>{o.codeName}</option>)}
                        </FormControl>
                    </div>
                </div>
                <div className={showPtoTypeCaption ? "row" : "hidden"}>
                    <div className="col-xs-12 col-md-12">
                        {/*<div className="caption" dangerouslySetInnerHTML={{ __html: ptoTypeCaption }}></div>*/}
                        <DynamicContent key={`typeCaption${pto.code}`} contentName={`typeCaption${pto.code}`}>
                            <div className="caption" dangerouslySetInnerHTML={{ __html: ptoTypeCaption }}></div>
                        </DynamicContent>
                    </div>
                </div>
                <div className={showReason ? "row" : "hidden"}>
                    <div className="col-xs-12 col-md-8 ap-pt-1">
                        <ControlLabel>Reason for Request: <span className="ap-color-red">*</span></ControlLabel>
                        <FormControl componentClass="select"
                            defaultValue={pto.reasonCode}
                            disabled={readonly || pto.ptoId > 0}
                            onChange={(evt) => this.handleChange("reasonCode", evt.target.value)}>
                            <option key="" value="">-- Please Select --</option>
                            {ptoReasons.map(o => <option key={o.code} value={o.code}>{o.codeName}</option>)}
                        </FormControl>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-12 is-period">
                        <Checkbox checked={isPeriod}  disabled={readonly} onChange={this.togglePeriod}>More Than One Day</Checkbox>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-6 col-md-4">
                        <ControlLabel>Date: <span className="ap-color-red">*</span></ControlLabel>
                        {
                            isMobile ?
                                <FormControl type="date" value={fromDate}
                                    onChange={(evt) => this.handleChange("fromDate", evt.target.value)}></FormControl>
                                :
                                <DatePicker dateFormat="MM/DD/YYYY" placeholder="MM/DD/YYYY"
                                    value={fromDate} disabled={readonly}
                                    onChange={(val, formattedVal) => this.handleChange("fromDate", moment(val).format("YYYY-MM-DD"))}
                                    onInput={(val, formattedVal) => this.handleChange("fromDate", moment(val).format("YYYY-MM-DD"))}
                                    showClearButton={false} />
                        }
                    </div>
                    <div className={isPeriod ? "col-xs-6 col-md-4" : "hidden"}>
                        <ControlLabel>End Date: <span className="ap-color-red">*</span></ControlLabel>
                        {
                            isMobile ?
                                <FormControl type="date" value={toDate}
                                    onChange={(evt) => this.handleChange("toDate", evt.target.value)}></FormControl>
                                :
                                <DatePicker dateFormat="MM/DD/YYYY" placeholder="MM/DD/YYYY"
                                    value={toDate} disabled={readonly}
                                    onChange={(val, formattedVal) => this.handleChange("toDate", moment(val).format("YYYY-MM-DD"))}
                                    onInput={(val, formattedVal) => this.handleChange("toDate", moment(val).format("YYYY-MM-DD"))}
                                    showClearButton={false} />
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-12 ap-pt-1">
                        <ControlLabel>
                            {isPeriod ? "Hours Requesting Per Day" : "Hours Requesting"}:
                            <span className="ap-color-red">*</span>
                            <span className="ap-ml-1 caption">{pto.unitPerDay} hrs scheduled per day</span>
                        </ControlLabel>
                        <FormControl type="text" style={{ width: "100px", marginBottom: "0px" }} 
                            disabled={readonly || fullDayIncrement}
                            onChange={(evt) => this.handleChange("unitPerDay", evt.target.value)} defaultValue={util.toShortHmString(pto.unitPerDay)}></FormControl>

                        {showPtoUnitCaption ? <div className="caption" dangerouslySetInnerHTML={{ __html: ptoUnitCaption }}></div> : null}
                    </div>
                </div>
                <div className={isPeriod ? "row" : "hidden"}>
                    <div className="col-xs-12 col-md-12 ap-pt-1 caption">
                        <i>** Deselect day(s) that will not be Time-Off.</i>
                    </div>
                </div>
                <div className={isPeriod ? "row" : "hidden"}>
                    {weeks}
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-9 ap-pt-1">
                        { accrualSummary }
                        {/* <div className="col-xs-12 col-md-12 dashboard">
                            <div className="row">
                                <div className="col-xs-12 col-md-12">
                                    <b>Leave Balance</b>
                                </div>
                            </div>
                            <div className="row dashboard-header">
                                <div className="col-xs-4 col-md-4">
                                    Type
                                </div>
                                <div className="col-xs-3 col-md-3">
                                    Accrued Hrs
                                </div>
                                <div className="col-xs-2 col-md-2">
                                    Taken Hrs
                                </div>
                                <div className="col-xs-3 col-md-3">
                                    Available Hrs
                                </div>
                            </div>
                            {visibleBalances}
                        </div> */}
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 col-md-12 ap-px-0">
                        <hr />
                    </div>
                </div>
                <div className={isSupervisor ? "row" : "hidden"}>
                    <div className="col-xs-12 col-md-12">
                        <ControlLabel>Supervisor Notes: <span className="ap-color-red">*</span></ControlLabel>
                        <FormControl componentClass="textarea" rows="3" defaultValue={pto ? pto.supervisorNotes : null} disabled={readonly}
                            onChange={(evt) => this.handleChange("supervisorNotes", evt.target.value)} />
                    </div>
                </div>
                <div className="row">
                    <div className={isMobile ? "col-xs-12" : "col-md-12 button-panel"}>
                        {arrButtons}
                    </div>
                </div>
                <div className={errors.length > 0 ? "row" : "hidden"}>
                    <div className="col-xs-12 col-md-12 error-panel">
                        <Alert bsStyle="danger">
                            <ul>{errors.map(m => <li key={m}>{m}</li>)} </ul>
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }
}
